import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { editRequestService, EditRequestDescription } from '../../services/editRequest.service';
import { CompactHeader } from '../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../components/CompactFooter/CompactFooter';

/**
 * Public landing page for the SP's approve/reject links in the re-edit request e-mail
 * (template 38). Route: /re-edit/:token/:action? — the backend endpoints it calls are
 * token-authenticated and whitelisted, so no login is required (the SP is not a portal user).
 *
 * Java-side equivalent of Laravel's re-edit-approve-confirm / re-edit-reject-confirm blades.
 */
export const ReEditAction: React.FC = () => {
  const { token, action } = useParams<{ token: string; action?: string }>();

  const [request, setRequest] = useState<EditRequestDescription | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ ok: boolean; message: string } | null>(null);

  // The e-mail (template 38) sends TWO links — /approve and /reject — so by the time the SP lands
  // here the decision is already made. This page confirms it; it does not ask again. Offering both
  // radios a second time was confusing and let a mis-click silently invert the decision.
  //
  // `null` = the bare /re-edit/:token route with no action segment. No e-mail produces that link,
  // but the route exists, so it still gets the two-way chooser rather than a guess.
  const linkedMode: 'approve' | 'reject' | null =
    action === 'approve' ? 'approve' : action === 'reject' ? 'reject' : null;
  // An unrecognised action segment (a typo, a mangled link) must NOT fall through to Approve —
  // that was the old default, and approving on a typo is unrecoverable from this page.
  const badAction = !!action && linkedMode === null;
  const [chosenMode, setChosenMode] = useState<'approve' | 'reject'>('approve');
  const mode = linkedMode ?? chosenMode;

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid request link.');
      return;
    }
    if (badAction) {
      setLoadError('This link is not valid. Please use the Approve or Reject button in the e-mail.');
      return;
    }
    editRequestService
      .describe(token)
      .then(setRequest)
      .catch((e) => setLoadError(e.response?.data?.message || 'Invalid or expired request link.'));
  }, [token, badAction]);

  const decide = async () => {
    if (!token) return;
    if (mode === 'reject' && remarks.trim().length < 5) {
      setDone(null);
      alert('Please provide brief remarks for the rejection.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = mode === 'approve'
        ? await editRequestService.approve(token)
        : await editRequestService.reject(token, remarks.trim());
      setDone({ ok: true, message: resp.message });
    } catch (e: any) {
      setDone({ ok: false, message: e.response?.data?.message || 'Could not record your decision. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyActioned = request && request.status !== 'PENDING';

  return (
    <>
      <CompactHeader />
      <section className="section-padding" style={{ minHeight: '60vh' }}>
        <div className="container" style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 28 }}>
            <h2 style={{ marginTop: 0, fontSize: 20 }}>Information Re-Edit Request</h2>

            {loadError && <p style={{ color: '#be1717' }}>{loadError}</p>}

            {!loadError && !request && <p>Loading request…</p>}

            {request && (
              <>
                <p style={{ margin: '4px 0' }}>
                  <strong>Investor:</strong> {request.investorName} ({request.investorUniqueId})
                </p>
                <div style={{ background: '#f8f9fa', borderLeft: '4px solid #6c757d', padding: 14, borderRadius: 4, margin: '14px 0' }}>
                  <strong>Reason given:</strong>
                  <p style={{ margin: '6px 0 0' }}>{request.reason}</p>
                </div>

                {alreadyActioned && (
                  <p style={{ color: '#2563eb' }}>
                    This request has already been {request.status.toLowerCase()}. No further action is needed.
                  </p>
                )}

                {done && (
                  <p style={{ color: done.ok ? '#10b981' : '#be1717', fontWeight: 600 }}>{done.message}</p>
                )}

                {!alreadyActioned && !done?.ok && (
                  <>
                    {linkedMode ? (
                      <p style={{ margin: '18px 0 10px', fontSize: 15, fontWeight: 700,
                                  color: linkedMode === 'approve' ? '#16794c' : '#be1717' }}>
                        You chose to {linkedMode === 'approve' ? 'approve' : 'reject'} this request.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', gap: 12, margin: '18px 0 10px' }}>
                        <label style={{ fontWeight: mode === 'approve' ? 700 : 400, cursor: 'pointer' }}>
                          <input type="radio" checked={mode === 'approve'} onChange={() => setChosenMode('approve')} /> Approve
                        </label>
                        <label style={{ fontWeight: mode === 'reject' ? 700 : 400, cursor: 'pointer' }}>
                          <input type="radio" checked={mode === 'reject'} onChange={() => setChosenMode('reject')} /> Reject
                        </label>
                      </div>
                    )}

                    {mode === 'approve' ? (
                      <p style={{ fontSize: 13, color: '#475569' }}>
                        Approving unlocks this investor's submitted information for this journey only.
                        It locks again when they re-submit.
                      </p>
                    ) : (
                      <textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Remarks for the investor (required)"
                        rows={3}
                        style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: 10, fontSize: 13 }}
                      />
                    )}

                    <button
                      type="button"
                      onClick={decide}
                      disabled={submitting}
                      style={{
                        marginTop: 14, padding: '10px 26px', border: 0, borderRadius: 22, color: '#fff',
                        fontWeight: 700, cursor: 'pointer',
                        background: mode === 'approve' ? '#28a745' : '#dc3545',
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      {submitting ? 'Submitting…' : mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default ReEditAction;
